import { AdjustmentMode } from '@local/lib';

import type { QualityFormulaPreviewResult } from '../../qualityFormulaTypes';

import { ADJUSTMENT_MODE_COLOR } from '../../qualityFormulaMetadata';

// maximumFractionDigits must be high enough to show the spec's own "Round To" precision (0-10) —
// capping this too low (e.g. 2) would re-truncate values the API already rounded more precisely.
export const formatNumber = (value: number): string => value.toLocaleString('en-US', { maximumFractionDigits: 6 });

// Puts the minus sign before the currency symbol (e.g. "-$20" instead of "$-20").
export const formatCurrency = (value: number): string =>
  value < 0 ? `-$${formatNumber(Math.abs(value))}` : `$${formatNumber(value)}`;

// Reuses the one shared Discount/Premium color pair (ADJUSTMENT_MODE_COLOR) rather than a second,
// independently-maintained red/green pair — 'adjusted' has no AdjustmentMode equivalent (it covers
// single-row formula types with no discount/premium split), so it falls back to a neutral
// (non-business-coded) amber palette hook instead of reusing Discount's or Premium's meaning.
export const getDirectionColor = (direction: QualityFormulaPreviewResult['direction']): string => {
  if (direction === 'discount') return ADJUSTMENT_MODE_COLOR[AdjustmentMode.Discount];
  if (direction === 'premium') return ADJUSTMENT_MODE_COLOR[AdjustmentMode.Premium];
  return 'var(--slds-g-color-palette-orange-30, #5f3e02)';
};

// Quantity results are always framed as a reduction, so a zero-unit result isn't actually a
// discount — only color it as one once a real reduction has occurred.
export const getTotalImpactColor = (result: QualityFormulaPreviewResult): string => {
  const isReduction = result.kind === 'price' || (result.totalImpact ?? 0) > 0;
  return isReduction ? getDirectionColor(result.direction) : 'var(--slds-g-color-on-surface-1, #757575)';
};
